import type { ElementType } from 'react';
import { Mail } from '@/assets/icons/interview-plan';
import { Atom01 } from '@kula-ai/icons/dual-tone/Atom01';
import { Link01 } from '@kula-ai/icons/dual-tone/Link01';
import { Calendar } from '@kula-ai/icons/dual-tone/Calendar';
import { getActivityKindIcon } from '@/components/automation/MoveWithAutomationModal/activityKindIcon';
import type { UnderwayActivity } from './types';

// 3rd-party assessment kinds — these render a "using <provider> [icon]" clause
// instead of going straight to the candidate. Mirrors the FE automatable set.
export const EXTERNAL_ASSESSMENT_KINDS = [
  'hackerrank',
  'hackerearth_assessment',
  'criteriacorp_assessment',
  'codeinterview_assessment',
  'hireflix_assessment',
] as const;

const PROVIDER_NAME_BY_KIND: Record<string, string> = {
  hackerrank: 'HackerRank',
  hackerearth_assessment: 'HackerEarth',
  criteriacorp_assessment: 'Criteria',
  codeinterview_assessment: 'CodeInterview',
  hireflix_assessment: 'Hireflix',
};

const isExternalAssessment = (kind: string): boolean =>
  (EXTERNAL_ASSESSMENT_KINDS as readonly string[]).includes(kind);

export const getProviderName = (kind: string): string =>
  PROVIDER_NAME_BY_KIND[kind] ?? '3rd party';

// Interview scheduling method (BE `automation_mode`) → short display label.
const SCHEDULE_MODE_LABELS: Record<string, string> = {
  self_schedule: 'self-schedule link',
  request_availability: 'availability request',
  ai_coordinator: 'Kula Coordinator',
};

export const getScheduleModeLabel = (mode: string | undefined): string =>
  (mode && SCHEDULE_MODE_LABELS[mode]) || '';

// Icon for the interview "using <mode>" clause — kept in lockstep with the
// automation (workflow) builder's per-scheduler icons (`SCHEDULE_ICONS` in
// `automation/activityIcons.ts`) so a mode reads identically in the popup and
// the builder: self_schedule → Link01, request_availability → Calendar,
// ai_coordinator → Atom01.
const SCHEDULE_MODE_ICONS: Record<string, ElementType> = {
  self_schedule: Link01,
  request_availability: Calendar,
  ai_coordinator: Atom01,
};

export const getScheduleModeIcon = (mode: string | undefined): ElementType =>
  (mode && SCHEDULE_MODE_ICONS[mode]) || Mail;

export type ActivitySubjectPreposition = 'to' | 'for' | 'on';

// The optional clause between the candidate and the "in the <stage>" tail.
// A kind has at most one: reviewer/user recipients (review, HM survey) OR a
// "using <X>" phrase (interview scheduling mode, 3rd-party provider).
export type ActivityExtraClause =
  | { type: 'recipient'; noun: 'reviewer' | 'hiring manager' }
  | { type: 'using'; label: string; icon: ElementType }
  | null;

export type ActivityCopyConfig = {
  // Present-progressive while the row is active (pending/running), past tense
  // once it has fired (status 'completed'):
  //   immediate → 'Sending' → 'Sent' (interview: 'Scheduling' → 'Scheduled')
  //   delayed   → 'Queuing' → 'Queued'
  verb: 'Sending' | 'Sent' | 'Scheduling' | 'Scheduled' | 'Queuing' | 'Queued';
  // Immediate rows use a short preposition ('to'/'for'/'on'); delayed rows use
  // the future-passive form ('to be sent to' / 'to be sent on' / 'to be sent
  // for'), so this is a free string.
  subjectPreposition: string;
  extra: ActivityExtraClause;
  // Single HM (employee) survey: the subject IS the recipient (hiring
  // manager), so the sentence names that person and skips the candidate +
  // extra clause.
  subjectIsRecipient?: boolean;
};

// The per-kind sentence shape for the underway row. Delayed rows (CORE-4986)
// swap the verb to Scheduling/Scheduled, use the future-passive preposition,
// and gain an "after <delay>" clause (rendered by UnderwayItemDescription).
export const getActivityCopyConfig = (
  activity: Pick<
    UnderwayActivity,
    'activityKind' | 'details' | 'delayed' | 'isBulk' | 'status'
  >
): ActivityCopyConfig =>
  activity.delayed
    ? getDelayedCopyConfig(activity)
    : getImmediateCopyConfig(activity);

// Immediate (non-delayed) copy — verb flips to past tense once the action has
// fired (status 'completed', after the 15s undo window):
//   email / assessment / candidate-survey → "Sending/Sent X to <cand> …"
//   interview                             → "Scheduling/Scheduled X for <cand> using ✉<mode> …"
//   review                                → "Sending/Sent X for <cand> to 👤<reviewer> …"
//   HM survey (single)                    → "Sending/Sent X to 👤<hm> …" (recipient IS subject)
//   HM survey (bulk)                      → "Sending/Sent X for N candidates to 👤<hm>s …"
//   3rd-party assessment                  → "Sending/Sent X to <cand> using [icon]<provider> …"
const getImmediateCopyConfig = (
  activity: Pick<
    UnderwayActivity,
    'activityKind' | 'details' | 'isBulk' | 'status'
  >
): ActivityCopyConfig => {
  const kind = activity.activityKind;
  // Done once the trigger has fired (UnderwayTimerManager: pending → running →
  // 'completed'). Interviews get scheduled, everything else gets sent.
  const isDone = activity.status === 'completed';
  const sendVerb: ActivityCopyConfig['verb'] = isDone ? 'Sent' : 'Sending';
  const scheduleVerb: ActivityCopyConfig['verb'] = isDone
    ? 'Scheduled'
    : 'Scheduling';

  if (kind === 'interview') {
    const mode = activity.details?.scheduleMode;
    const label = getScheduleModeLabel(mode);
    return {
      verb: scheduleVerb,
      subjectPreposition: 'for',
      // Skip the clause when the mode is unknown rather than print "using".
      extra: label
        ? { type: 'using', label, icon: getScheduleModeIcon(mode) }
        : null,
    };
  }

  if (kind === 'review') {
    return {
      verb: sendVerb,
      subjectPreposition: 'for',
      extra: { type: 'recipient', noun: 'reviewer' },
    };
  }

  if (kind === 'survey') {
    if (activity.details?.surveyType === 'employee') {
      // Single HM survey → the hiring manager is the subject; no candidate.
      if (activity.isBulk !== true) {
        return {
          verb: sendVerb,
          subjectPreposition: 'to',
          extra: null,
          subjectIsRecipient: true,
        };
      }
      return {
        verb: sendVerb,
        subjectPreposition: 'for',
        extra: { type: 'recipient', noun: 'hiring manager' },
      };
    }
    // Candidate survey (default): sent straight to the candidate.
    return { verb: sendVerb, subjectPreposition: 'to', extra: null };
  }

  if (isExternalAssessment(kind)) {
    return {
      verb: sendVerb,
      subjectPreposition: 'to',
      extra: {
        type: 'using',
        label: getProviderName(kind),
        icon: getActivityKindIcon(kind),
      },
    };
  }

  // email, assessment (Kula), and any other "send to the candidate" kind.
  return { verb: sendVerb, subjectPreposition: 'to', extra: null };
};

// Delayed (queued first-step) copy — PM matrix. Verb reads "Queuing" while the
// loader is active (pending/running/paused) and "Queued" once it's filled
// (completed); prepositions take the future-passive form.
//   email / assessment / candidate-survey → "Queuing X to be sent to <cand> …"
//   interview                             → "Queuing X to be scheduled for <cand> using ✉<mode> …"
//   review                                → "Queuing X to be sent for <cand> to 👤<reviewer> …"
//   HM survey (single)                    → "Queuing X to be sent to 👤<hm> …" (recipient IS subject)
//   HM survey (bulk)                      → "Queuing X to be sent for N candidates to 👤<hm>s …"
//   3rd-party assessment                  → "Queuing X to be sent to <cand> using [icon]<provider> …"
const getDelayedCopyConfig = (
  activity: Pick<
    UnderwayActivity,
    'activityKind' | 'details' | 'isBulk' | 'status'
  >
): ActivityCopyConfig => {
  const kind = activity.activityKind;
  const isBulk = activity.isBulk === true;
  const verb: ActivityCopyConfig['verb'] =
    activity.status === 'completed' ? 'Queued' : 'Queuing';

  if (kind === 'interview') {
    const mode = activity.details?.scheduleMode;
    const label = getScheduleModeLabel(mode);
    return {
      verb,
      subjectPreposition: 'to be scheduled for',
      extra: label
        ? { type: 'using', label, icon: getScheduleModeIcon(mode) }
        : null,
    };
  }

  if (kind === 'review') {
    return {
      verb,
      subjectPreposition: 'to be sent for',
      extra: { type: 'recipient', noun: 'reviewer' },
    };
  }

  if (kind === 'survey') {
    if (activity.details?.surveyType === 'employee') {
      // Single HM survey → the recipient (hiring manager) is the subject; no
      // candidate, no extra clause.
      if (!isBulk) {
        return {
          verb,
          subjectPreposition: 'to be sent to',
          extra: null,
          subjectIsRecipient: true,
        };
      }
      // Bulk HM survey → "to be sent for N candidates to M hiring managers".
      return {
        verb,
        subjectPreposition: 'to be sent for',
        extra: { type: 'recipient', noun: 'hiring manager' },
      };
    }
    // Candidate survey.
    return { verb, subjectPreposition: 'to be sent to', extra: null };
  }

  if (isExternalAssessment(kind)) {
    return {
      verb,
      subjectPreposition: 'to be sent to',
      extra: {
        type: 'using',
        label: getProviderName(kind),
        icon: getActivityKindIcon(kind),
      },
    };
  }

  // email, assessment (Kula), and any other "send to the candidate" kind.
  return { verb, subjectPreposition: 'to be sent to', extra: null };
};
